import { Axios } from '@src/app/context/Api/includes/Axios';
import { logger } from '@src/shared/includes/logger';
import { config } from '@uhg-abyss/web/tools/config';
import { AxiosHeaders, AxiosRequestConfig } from 'axios';
import { isEmpty, isEqual, omit, pick } from 'lodash';

import { DefaultListEventsPayload, DefaultListEventsQueryPayload } from './includes/defaults';
import { ListEventsFiltersSchema, ListEventsSchema } from './includes/schema';
import { ListEventsData, ListEventsQueryPayload } from './includes/types';

interface CustomRequestHeaders extends AxiosHeaders {
  'x-api-endpoint': string;
}

/**
 * Query: ListEvents
 *
 * Retrieves a list of events from the remote API.
 * Uses GET for simple pagination queries, and POST for filtered queries.
 * Validates request and response data using Zod schemas.
 * Logs errors and rethrows them for upstream handling.
 */
export const ListEvents = async (
  payload: ListEventsQueryPayload = DefaultListEventsQueryPayload
): Promise<ListEventsData> => {
  try {
    const [theApiKey, theQueryKey, thePayload] = payload;

    const body = omit(thePayload, ['page', 'size', 'sort']);
    const pagination = pick(thePayload, ['page', 'size', 'sort']);

    const requestArgs: AxiosRequestConfig = {
      baseURL: config('API_URL'),
      headers: { 'x-api-endpoint': '/events' } as CustomRequestHeaders,
      method: 'GET',
      params: { apiKey: theApiKey, queryKey: theQueryKey, ...pagination },
    };

    if (!isEmpty(body) && !isEqual(body, DefaultListEventsPayload)) {
      requestArgs.headers = { 'x-api-endpoint': '/events/search' } as CustomRequestHeaders;
      requestArgs.method = 'POST';
      requestArgs.data = body;
      ListEventsFiltersSchema.parse(body);
    }

    const remoteResponse = await Axios.request(requestArgs);
    return ListEventsSchema.parse(remoteResponse.data);
  } catch (error) {
    logger.error('src/entities/Event/api/requests/queries/ListEvents/ListEvents.ts -> ListEvents()', false, error);
    throw error;
  }
};
